import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { BillingApi } from '../api';
import { BillingPlans, BillingSummary } from '../types';

/**
 * Query keys for billing
 */
export const billingKeys = {
  all: ['billing'] as const,
  summary: () => [...billingKeys.all, 'summary'] as const,
  plans: () => [...billingKeys.all, 'plans'] as const,
};

/** The server re-reads Login every few minutes anyway; a minute here saves repeat calls across screens. */
const SUMMARY_STALE_TIME_MS = 60_000;

/**
 * Hook to get the user's plan, limits, usage and trials
 */
export function useBillingSummary(
  billingApi: BillingApi,
  options?: Omit<UseQueryOptions<BillingSummary, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BillingSummary, Error>({
    queryKey: billingKeys.summary(),
    queryFn: () => billingApi.getSummary(),
    staleTime: SUMMARY_STALE_TIME_MS,
    ...options,
  });
}

/**
 * Hook to get the plans that can be bought
 */
export function useBillingPlans(
  billingApi: BillingApi,
  options?: Omit<UseQueryOptions<BillingPlans, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<BillingPlans, Error>({
    queryKey: billingKeys.plans(),
    queryFn: () => billingApi.getPlans(),
    staleTime: SUMMARY_STALE_TIME_MS,
    ...options,
  });
}
